import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAlert } from 'react-alert';

import { useActions } from '../../../../../hooks/useActions';
import { useModalDetails } from '../../../../../hooks/useModalDetails';
import { useProcessing } from '../../../../../hooks/useProcessing';
import { useCancellationToken } from '../../../../../hooks/useTokenCancellation';
import { updateModalState } from '../../../../../store/redux-slices/modals';
import { personMembershipIsActiveByMembershipIdSelector } from '../../../../../store/redux-slices/people/selectors';
import { DELETE_MEMBERSHIP_PROCESSING } from '../../../../../store/redux-slices/processes/constants';
import { DELETE_DOC_MEMBERSHIP_MODAL_NAME } from './deleteDOCMembershipModal.const';
import { DeleteDOCMembershipModalDetails } from './deleteDOCMembershipModal.models';
import { deleteDOCMembership } from '../../../../../store/saga-slices/membership/dutyOfCare';
import { IDeleteDOCMembershipPayload } from '../../../../../store/saga-slices/membership/dutyOfCare/models';

type UseDeleteDOCMembershipModalProps = {
  id?: string;
};

export const useDeleteDOCMembershipModal = ({
  id,
}: UseDeleteDOCMembershipModalProps) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const cancellationToken = useCancellationToken();
  const isDeleting = useProcessing(DELETE_MEMBERSHIP_PROCESSING);
  const UpdateModalState = useActions(updateModalState);
  const DeleteMembership = useActions(deleteDOCMembership);

  const {
    membershipId,
    productType,
    personName,
  }: DeleteDOCMembershipModalDetails = useModalDetails(
    DELETE_DOC_MEMBERSHIP_MODAL_NAME + id,
  );

  const personMembershipIsActive = useSelector(
    personMembershipIsActiveByMembershipIdSelector(membershipId),
  );

  const onBackHandler = () => {
    UpdateModalState(DELETE_DOC_MEMBERSHIP_MODAL_NAME + id);
  };

  const deleteMembershipHandler = useCallback(() => {
    const payload: IDeleteDOCMembershipPayload = {
      membershipId,
      cancellationToken,
      isActive: personMembershipIsActive,
      success: () => {
        alert.success(t('alerts.membership-delete-success'));
        UpdateModalState(DELETE_DOC_MEMBERSHIP_MODAL_NAME + id);
      },
      error: () => {
        alert.error(t('alerts.membership-delete-error'));
      },
    };
    DeleteMembership(payload);
  }, [
    membershipId,
    cancellationToken,
    personMembershipIsActive,
    DeleteMembership,
    alert,
    t,
    UpdateModalState,
    id,
  ]);

  return {
    onBackHandler,
    productType,
    personName,
    deleteMembershipHandler,
    isDeleting,
    personMembershipIsActive,
  };
};
